// FAQ 2026 (new_design_faq_2026) — interactions scoped to .faq-2026.
// Ported from the ticket prototype (thoughts/shared/tickets/ENG-7782/index (5).html):
// scroll-reveal, chip smooth-scroll, one-open-at-a-time per category, and the search
// (debounce, tag-safe highlight, count, empty state, clear button, Escape).
// All queries run against `root` (not document) and the prototype's element ids are
// replaced by `.js-faq-search*` class hooks. This section is single-instance per page:
// `querySelector` grabs the first `.faq-2026`, and the template only renders one.
(function () {
  const root = document.querySelector('.faq-2026');
  if (!root) return;

  // ── Scroll reveal ──
  // Only opt into the hidden-then-reveal behaviour when we can actually drive it.
  // Adding `.reveal-on` is what makes the CSS hide the elements, so if this JS never
  // runs the content stays visible (see faq-2026.css). Guard on IntersectionObserver
  // so unsupported browsers keep everything visible too.
  const reveals = root.querySelectorAll('.reveal');
  if (reveals.length && 'IntersectionObserver' in window) {
    root.classList.add('reveal-on');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    reveals.forEach((el) => observer.observe(el));
  }

  // ── Smooth-scroll for chip links, accounting for the sticky global nav ──
  root.querySelectorAll('a[href^="#"]').forEach((link) => {
    link.addEventListener('click', (e) => {
      const id = link.getAttribute('href');
      if (!id || id === '#') return;
      const target = root.querySelector(id);
      if (!target) return;
      e.preventDefault();
      const y = target.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top: y, behavior: 'smooth' });
    });
  });

  // ── Only one FAQ open at a time within each category ──
  root.querySelectorAll('.cat').forEach((cat) => {
    const detailsList = cat.querySelectorAll('details');
    detailsList.forEach((d) => {
      d.addEventListener('toggle', () => {
        if (d.open) {
          detailsList.forEach((o) => { if (o !== d) o.open = false; });
        }
      });
    });
  });

  // ── FAQ search ─────────────────────────────────────
  (function initSearch() {
    const input = root.querySelector('.js-faq-search');
    const clear = root.querySelector('.js-faq-search-clear');
    const count = root.querySelector('.js-faq-search-count');
    const empty = root.querySelector('.js-faq-empty');
    const wrap  = root.querySelector('.utility');
    if (!input || !wrap) return;

    const cats  = Array.from(root.querySelectorAll('.cat'));
    const items = Array.from(root.querySelectorAll('.faq-item'));

    // Cache the original question + answer HTML (to un-highlight) and the lowercased
    // text (to match against). Keyed off the item node via a WeakMap rather than
    // expando properties, so nothing is attached to the DOM elements themselves.
    const cache = new WeakMap();
    items.forEach((item) => {
      const q = item.querySelector('.faq-q');
      const a = item.querySelector('.faq-a');
      cache.set(item, {
        origQ: q ? q.innerHTML : '',
        origA: a ? a.innerHTML : '',
        text: ((q ? q.textContent : '') + ' ' + (a ? a.textContent : '')).toLowerCase(),
      });
    });

    const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const escapeHTML  = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

    function highlightNode(html, terms) {
      // Split HTML into tag + text segments; only touch text nodes.
      return html.replace(/(<[^>]+>)|([^<]+)/g, (m, tag, text) => {
        if (tag) return tag;
        let out = text;
        terms.forEach((t) => {
          if (!t) return;
          const re = new RegExp(escapeRegex(t), 'gi');
          out = out.replace(re, (match) => `<span class="faq-highlight">${escapeHTML(match)}</span>`);
        });
        return out;
      });
    }

    function reset() {
      wrap.classList.remove('active');
      if (count) count.textContent = '';
      if (empty) empty.classList.remove('visible');
      cats.forEach((c) => c.classList.remove('hidden'));
      items.forEach((item) => {
        item.classList.remove('hidden');
        const data = cache.get(item);
        const q = item.querySelector('.faq-q');
        const a = item.querySelector('.faq-a');
        if (q && data) q.innerHTML = data.origQ;
        if (a && data) a.innerHTML = data.origA;
        const details = item.querySelector('details');
        if (details) details.open = false;
      });
    }

    function apply(query) {
      const q = query.trim().toLowerCase();
      if (!q) { reset(); return; }

      wrap.classList.add('active');

      const terms = q.split(/\s+/).filter(Boolean);
      let matched = 0;

      items.forEach((item) => {
        const data = cache.get(item);
        const hit = data && terms.every((t) => data.text.indexOf(t) !== -1);
        item.classList.toggle('hidden', !hit);
        const qEl = item.querySelector('.faq-q');
        const aEl = item.querySelector('.faq-a');
        const details = item.querySelector('details');
        if (hit) {
          matched++;
          if (qEl) qEl.innerHTML = highlightNode(data.origQ, terms);
          if (aEl) aEl.innerHTML = highlightNode(data.origA, terms);
          if (details) details.open = true;
        } else {
          if (qEl && data) qEl.innerHTML = data.origQ;
          if (aEl && data) aEl.innerHTML = data.origA;
          if (details) details.open = false;
        }
      });

      cats.forEach((cat) => {
        const anyHit = cat.querySelectorAll('.faq-item:not(.hidden)').length > 0;
        cat.classList.toggle('hidden', !anyHit);
        if (anyHit) cat.classList.add('visible'); // keep scroll-reveal from hiding it
      });

      if (matched === 0) {
        if (empty) empty.classList.add('visible');
        if (count) count.textContent = 'No matches';
      } else {
        if (empty) empty.classList.remove('visible');
        if (count) count.textContent = matched + (matched === 1 ? ' answer' : ' answers');
      }
    }

    let t;
    input.addEventListener('input', () => {
      clearTimeout(t);
      t = setTimeout(() => apply(input.value), 120);
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { input.value = ''; reset(); input.blur(); }
    });
    if (clear) {
      clear.addEventListener('click', () => {
        input.value = '';
        reset();
        input.focus();
      });
    }
  })();
})();
